/**
 * RECRUITER STREAK COUNTER
 *
 * Displays HR user's current recruiting streak with animated briefcase icon
 * Shows streak milestones and provides visual feedback
 * Professional business theme for recruiters
 */

import React from 'react';
import { Briefcase, Calendar, Award, Shield } from 'lucide-react';

interface RecruiterStreakCounterProps {
  currentStreak: number;
  longestStreak: number;
  freezeTokens: number;
  lastActivityDate?: Date;
  compact?: boolean;
  className?: string;
}

const RecruiterStreakCounter: React.FC<RecruiterStreakCounterProps> = ({
  currentStreak,
  longestStreak,
  freezeTokens,
  lastActivityDate,
  compact = false,
  className = ''
}) => {
  // Calculate briefcase color intensity based on streak
  const getBriefcaseIntensity = (streak: number): string => {
    if (streak >= 100) return 'text-purple-600 drop-shadow-[0_0_15px_rgba(147,51,234,0.7)]';
    if (streak >= 30)  return 'text-blue-600 drop-shadow-[0_0_12px_rgba(37,99,235,0.7)]';
    if (streak >= 14)  return 'text-indigo-600 drop-shadow-[0_0_10px_rgba(79,70,229,0.7)]';
    if (streak >= 7)   return 'text-cyan-600 drop-shadow-[0_0_8px_rgba(8,145,178,0.7)]';
    if (streak >= 3)   return 'text-teal-600 drop-shadow-[0_0_6px_rgba(13,148,136,0.7)]';
    return 'text-gray-400';
  };

  // Get motivational message based on streak
  const getStreakMessage = (streak: number): string => {
    if (streak === 0)   return '💼 Start recruiting today!';
    if (streak === 1)   return '✨ Great start! Keep hiring!';
    if (streak < 7)     return '💪 Building momentum!';
    if (streak < 14)    return '🎯 Consistent recruiter!';
    if (streak < 30)    return '🔥 Hiring champion!';
    if (streak < 60)    return '🧲 Talent magnet!';
    if (streak < 100)   return '⭐ Elite recruiter!';
    return '👑 LEGENDARY RECRUITER!';
  };

  // Define streak milestones for recruiters
  const getNextMilestone = (streak: number): { milestone: number; name: string } | null => {
    const milestones = [
      { milestone: 7,   name: '7-Day Recruiter' },
      { milestone: 14,  name: '14-Day Recruiter Master' },
      { milestone: 21,  name: '21-Day Hiring Pro' },
      { milestone: 30,  name: '30-Day Talent Leader' },
      { milestone: 60,  name: '60-Day Hiring Legend' },
      { milestone: 100, name: '100-Day Recruitment Titan' },
      { milestone: 365, name: '365-Day Hiring God' }
    ];

    return milestones.find(m => streak < m.milestone) || null;
  };

  const nextMilestone = getNextMilestone(currentStreak);
  const progressToNextMilestone = nextMilestone
    ? ((currentStreak / nextMilestone.milestone) * 100).toFixed(0)
    : 100;

  const briefcaseIntensity = getBriefcaseIntensity(currentStreak);

  // Compact version for embedding in other components
  if (compact) {
    return (
      <div className={`inline-flex items-center space-x-2 ${className}`}>
        <Briefcase className={`w-5 h-5 ${briefcaseIntensity} ${currentStreak > 0 ? 'animate-pulse' : ''}`} />
        <span className="text-lg font-bold text-gray-900">{currentStreak}</span>
        <span className="text-sm text-gray-600">day streak</span>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 ${className}`}>
      {/* Header with Freeze Tokens */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-bold text-gray-900">Your Recruiting Streak</h3>
        {freezeTokens > 0 && (
          <div className="flex items-center space-x-1 px-3 py-1 bg-blue-100 rounded-full">
            <Shield className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-semibold text-blue-900">
              {freezeTokens} {freezeTokens === 1 ? 'Freeze' : 'Freezes'}
            </span>
          </div>
        )}
      </div>

      {/* Main Streak Display with Briefcase */}
      <div className="text-center mb-6">
        <div className="relative inline-block">
          {/* Animated Briefcase Icon */}
          <Briefcase
            className={`w-24 h-24 mx-auto mb-4 ${briefcaseIntensity} ${
              currentStreak > 0 ? 'animate-briefcase-glow' : ''
            }`}
          />

          {/* Streak Number Overlay */}
          <div className="absolute inset-0 flex items-center justify-center pt-2 pointer-events-none">
      <span
        className="text-4xl font-black text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]"
        style={{
          color: currentStreak === 0 ? '#FFC107' : 'white', // yellow if 0
          textShadow:
            currentStreak === 0
              ? '0 0 10px #FFC107, 0 2px 6px rgba(0,0,0,0.5)'
              : '0 2px 4px rgba(0,0,0,0.5)',
        }}
      >
        {currentStreak}
      </span>
    </div>
  </div>

        {/* Streak Text */}
        <p className="text-2xl font-bold text-gray-900 mb-2">
          {currentStreak === 0 ? 'No streak yet' : `${currentStreak} Day${currentStreak !== 1 ? 's' : ''}`}
        </p>
        <p className="text-sm text-gray-600">{getStreakMessage(currentStreak)}</p>
      </div>

      {/* Next Milestone Progress Bar */}
      {nextMilestone && currentStreak > 0 && (
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-semibold text-gray-700">Next Milestone</span>
            <span className="text-sm text-gray-600">
              {nextMilestone.milestone - currentStreak} days to {nextMilestone.name}
            </span>
          </div>

          {/* Progress Bar */}
          <div className="relative w-full h-3 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-400 via-indigo-500 to-purple-600 transition-all duration-500"
              style={{ width: `${progressToNextMilestone}%` }}
            >
              {/* Shine effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-shine" />
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3">
        {/* Longest Streak Card */}
        <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Award className="w-4 h-4 text-indigo-600" />
            <span className="text-xs font-semibold text-indigo-900">Longest Streak</span>
          </div>
          <div className="text-2xl font-bold text-indigo-900">{longestStreak}</div>
          <div className="text-xs text-indigo-700">days</div>
        </div>

        {/* Last Activity Card */}
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Calendar className="w-4 h-4 text-blue-600" />
            <span className="text-xs font-semibold text-blue-900">Last Activity</span>
          </div>
          <div className="text-sm font-bold text-blue-900">
            {lastActivityDate
              ? lastActivityDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
              : 'Today'}
          </div>
          <div className="text-xs text-blue-700">
            {lastActivityDate
              ? new Date().toDateString() === lastActivityDate.toDateString()
                ? 'Today'
                : `${Math.floor((new Date().getTime() - lastActivityDate.getTime()) / (1000 * 60 * 60 * 24))} days ago`
              : 'Just now'}
          </div>
        </div>
      </div>

      {/* Streak Protection Info */}
      {currentStreak > 0 && (
        <div className="mt-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3">
          <p className="text-xs text-gray-700">
            {freezeTokens > 0 ? (
              <>
                💡 <span className="font-semibold">Tip:</span> You have {freezeTokens} streak{' '}
                {freezeTokens === 1 ? 'freeze' : 'freezes'} to protect your recruiting streak if you miss a day!
              </>
            ) : (
              <>
                ⚠️ <span className="font-semibold">Warning:</span> You have no streak freezes left.
                Don't miss tomorrow or your recruiting streak will reset!
              </>
            )}
          </p>
        </div>
      )}

      {/* Recruiting Activity Tips */}
      {currentStreak === 0 && (
        <div className="mt-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-3">
          <p className="text-xs text-gray-700">
            💼 <span className="font-semibold">Start your streak:</span> Post a job, review candidates,
            create tests, or shortlist talent to begin your recruiting streak!
          </p>
        </div>
      )}

      {/* CSS Animations */}
      <style>{`
        /* Briefcase bounce animation - professional dynamic effect */
        @keyframes briefcase-glow {
          0%, 100% {
            transform: scale(1) translateY(0) rotate(0deg);
            filter: brightness(1);
          }
          25% {
            transform: scale(1.05) translateY(-3px) rotate(-2deg);
            filter: brightness(1.15);
          }
          50% {
            transform: scale(0.98) translateY(2px) rotate(2deg);
            filter: brightness(1.05);
          }
          75% {
            transform: scale(1.02) translateY(-1px) rotate(-1deg);
            filter: brightness(1.1);
          }
        }

        .animate-briefcase-glow {
          animation: briefcase-glow 2.5s ease-in-out infinite;
        }

        /* Shine effect animation */
        @keyframes shine {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }

        .animate-shine {
          animation: shine 2s infinite;
        }
      `}</style>
    </div>
  );
};

export default RecruiterStreakCounter;

